import React, { useEffect, useState, useCallback } from "react";
import { FaGlobe, FaCheck } from "react-icons/fa";

import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
} from "firebase/firestore";

import { db } from "../firebase";
import "./LanguageSelector.css";

const LANGUAGES = [
  { code: "en", nativeName: "English" },
  { code: "fr", nativeName: "Français" },
  { code: "sw", nativeName: "Kiswahili" },
  { code: "pt", nativeName: "Português" },
  { code: "es", nativeName: "Español" },
  { code: "pt-AO", nativeName: "Português (Angola)" },
  { code: "ar", nativeName: "العربية" },
  { code: "zh", nativeName: "中文" },
  { code: "hi", nativeName: "हिन्दी" },
  { code: "bn", nativeName: "বাংলা" },
  { code: "ru", nativeName: "Русский" },
  { code: "de", nativeName: "Deutsch" },
  { code: "it", nativeName: "Italiano" },
  { code: "nl", nativeName: "Nederlands" },
  { code: "tr", nativeName: "Türkçe" },
  { code: "ja", nativeName: "日本語" },
  { code: "ko", nativeName: "한국어" },
  { code: "vi", nativeName: "Tiếng Việt" },
  { code: "th", nativeName: "ไทย" },
  { code: "id", nativeName: "Bahasa Indonesia" },
  { code: "ms", nativeName: "Bahasa Melayu" },
  { code: "ur", nativeName: "اردو" },
  { code: "fa", nativeName: "فارسی" },
  { code: "ha", nativeName: "Hausa" },
  { code: "yo", nativeName: "Yorùbá" },
  { code: "zu", nativeName: "isiZulu" },
  { code: "am", nativeName: "አማርኛ" },
  { code: "so", nativeName: "Soomaali" },
  { code: "rw", nativeName: "Ikinyarwanda" },
  { code: "lg", nativeName: "Luganda" },
  { code: "af", nativeName: "Afrikaans" },
];

const LANGUAGE_STORAGE_KEY = "language";
const isValidLanguage = (code) => LANGUAGES.some((item) => item.code === code);

const readStoredLanguage = () => {
  try {
    const candidates = [
      localStorage.getItem("language"),
      localStorage.getItem("appLanguage"),
      localStorage.getItem("lang"),
    ];
    return candidates.find(isValidLanguage) || "";
  } catch (error) {
    console.error("Failed to read language:", error);
    return "";
  }
};

export default function LanguageSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const [language, setLanguage] = useState(readStoredLanguage);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const notifyLanguageChange = useCallback((languageCode) => {
    window.dispatchEvent(
      new CustomEvent("languageChanged", {
        detail: { language: languageCode },
      })
    );
  }, []);

  // Keep this selector in sync if another component/tab changes the language.
  useEffect(() => {
    const handleLanguageChanged = (event) => {
      const nextLanguage =
        event?.detail?.language ||
        (typeof event?.detail === "string" ? event.detail : "");
      if (isValidLanguage(nextLanguage)) setLanguage(nextLanguage);
    };

    const handleStorage = (event) => {
      if (!event.key || ["language", "appLanguage", "lang"].includes(event.key)) {
        const nextLanguage = event.newValue && isValidLanguage(event.newValue)
          ? event.newValue
          : readStoredLanguage();
        if (nextLanguage) setLanguage(nextLanguage);
      }
    };

    window.addEventListener("languageChanged", handleLanguageChanged);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("languageChanged", handleLanguageChanged);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  // Load the account's saved language from Firestore.
  useEffect(() => {
    let cancelled = false;

    const loadLanguage = async () => {
      try {
        const username = localStorage.getItem("username");
        if (!username) return;

        const usersQuery = query(
          collection(db, "users"),
          where("username", "==", username)
        );
        const snapshot = await getDocs(usersQuery);
        if (cancelled || snapshot.empty) return;

        const firestoreLanguage = snapshot.docs[0].data()?.language;
        if (!isValidLanguage(firestoreLanguage)) return;

        setLanguage(firestoreLanguage);
        try {
          localStorage.setItem(LANGUAGE_STORAGE_KEY, firestoreLanguage);
        } catch (storageError) {
          console.error("Failed to save language locally:", storageError);
        }
        notifyLanguageChange(firestoreLanguage);
      } catch (error) {
        console.error("Failed to load language:", error);
      }
    };

    loadLanguage();
    return () => {
      cancelled = true;
    };
  }, [notifyLanguageChange]);

  const saveLanguage = async (languageCode) => {
    if (loading) return;
    if (!isValidLanguage(languageCode)) {
      setMessage("Invalid language selected.");
      return;
    }

    // Apply locally first so the whole app changes language immediately.
    setLanguage(languageCode);
    setMessage("");
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, languageCode);
    } catch (error) {
      console.error("Failed to save language locally:", error);
    }
    notifyLanguageChange(languageCode);
    setIsOpen(false);

    const username = localStorage.getItem("username");
    if (!username) {
      setMessage("Language changed on this device. Log in to save it to your account.");
      return;
    }

    setLoading(true);
    try {
      const usersQuery = query(
        collection(db, "users"),
        where("username", "==", username)
      );
      const snapshot = await getDocs(usersQuery);

      if (snapshot.empty) {
        setMessage("Language changed on this device, but user account was not found.");
        return;
      }

      await updateDoc(snapshot.docs[0].ref, { language: languageCode });
      setMessage("Language saved successfully.");
    } catch (error) {
      console.error("Error saving language:", error);
      setMessage("Language changed on this device, but could not be saved to your account.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="language-picker">
      <button
        type="button"
        className="language-picker-trigger"
        onClick={() => {
          setIsOpen((previous) => !previous);
          setMessage("");
        }}
        aria-label="Choose language"
        aria-expanded={isOpen}
        title="Choose language"
      >
        <FaGlobe />
      </button>

      {isOpen && (
        <div className="language-picker-menu">
          <div className="language-picker-heading">
            <FaGlobe />
            <span>Select Language</span>
          </div>

          <div className="language-picker-list">
            {LANGUAGES.map((item) => (
              <button
                key={item.code}
                type="button"
                className={`language-picker-option ${language === item.code ? "selected" : ""}`}
                onClick={() => saveLanguage(item.code)}
                disabled={loading}
                aria-pressed={language === item.code}
              >
                <span>{item.nativeName}</span>
                {language === item.code && <FaCheck />}
              </button>
            ))}
          </div>

          {loading && <p className="language-picker-message" role="status">Saving language...</p>}
          {!loading && message && <p className="language-picker-message" role="status">{message}</p>}
        </div>
      )}
    </div>
  );
}
